/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { USE_OBSERVABLE_STATE_PENDING } from '@squiz/dxp-console-support';
import {
  DataList,
  DataListItem,
  EmptyState,
  EmptyStateProps,
  ICON_CHEVRON_RIGHT,
  ICON_SITE_ADD,
  Loader,
  SdsHeading,
  SdsIcon,
  SdsText,
  SdsTextSize,
  SdsTextWeight,
} from '@squiz/sds';
import {
  EntityTenantSetting,
  TenantDomainValue,
  useTenantSiteList,
} from 'apps/console-core/src/Services/tenant-settings';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { STATIC_SITES_DATA_IDS } from '../../StaticSites.constants';
import { DomainStatusBadge } from '../DomainStatusBadge/DomainStatusBadge';

export const DomainList = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const labels = {
    addDomain: t(CONFIGURATION_STRINGS.addDomain),
    heading: t(CONFIGURATION_STRINGS.noDomains),
    staticSites: t(CONFIGURATION_STRINGS.staticSites),
    staticSitesListDescription: t(CONFIGURATION_STRINGS.staticSitesListDescription),
    subheading: t(CONFIGURATION_STRINGS.setUpDomain),
  };

  const filteringFn = (site: EntityTenantSetting<TenantDomainValue>): boolean | undefined =>
    site.value.staticHostingEnabled;

  const [siteList, state] = useTenantSiteList({ filteringFn });
  const loading = state === USE_OBSERVABLE_STATE_PENDING;

  const convertSiteToDataListItem = useCallback(
    ({ settingKey: url }: EntityTenantSetting<TenantDomainValue>): DataListItem => {
      const handleNavigation = (): void => {
        navigate(btoa(url));
      };

      const itemContent = (
        <div className="domain-list-item__content">
          <SdsText fontSize={SdsTextSize.Large} fontWeight={SdsTextWeight.Semibold} text={url} />
          <SdsIcon classes="domain-list-item__icon" svg={ICON_CHEVRON_RIGHT} />
          <DomainStatusBadge enabled />
        </div>
      );

      return {
        action: handleNavigation,
        content: itemContent,
        dataId: STATIC_SITES_DATA_IDS.staticSitesListButton,
        id: url,
        isCustom: true,
        key: url,
      };
    },
    [navigate],
  );

  if (loading || !siteList) {
    return (
      <div className="domain-list loading">
        <Loader />
      </div>
    );
  }

  const getDomainList = (): JSX.Element => {
    if (!siteList.length) {
      const emptyStateProps: EmptyStateProps = {
        caption: labels.subheading,
        dataId: STATIC_SITES_DATA_IDS.staticSitesListEmptyState,
        isLite: true,
        svg: ICON_SITE_ADD,
        title: labels.heading,
      };
      return <EmptyState {...emptyStateProps}></EmptyState>;
    }

    const domainListItems: Array<DataListItem> = siteList.map((site) => {
      return convertSiteToDataListItem(site);
    });

    return <DataList classes="domain-list" empty={<></>} isInteractive items={domainListItems} showHeader={false} />;
  };

  return (
    <div className="domain-list__wrapper">
      <SdsHeading classes="domain-list__heading" level={4} text={labels.staticSites} />
      <SdsText
        classes="domain-list__description"
        fontSize={SdsTextSize.Small}
        text={labels.staticSitesListDescription}
      />
      {getDomainList()}
    </div>
  );
};
